"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.broadcastTransaction = exports.getOperations = exports.getAccountParams = exports.getAccount = exports.getCroAmount = exports.getClient = void 0;
const chain_jslib_1 = require("@crypto-org-chain/chain-jslib");
const network_1 = __importDefault(require("@ledgerhq/live-network/network"));
const bignumber_js_1 = require("bignumber.js");
const env_1 = require("../../../env");
const operation_1 = require("../../../operation");
const logic_1 = require("../logic");
const sdk_types_1 = require("./sdk.types");
const PAGINATION_LIMIT = 200;
const instances = {};
/**
 * Get CroClient
 */
function getClient(currencyId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (instances[currencyId]) {
            return instances[currencyId];
        }
        const crypto_org_rpc_url = (0, logic_1.isTestNet)(currencyId)
            ? (0, env_1.getEnv)("CRYPTO_ORG_TESTNET_RPC_URL")
            : (0, env_1.getEnv)("CRYPTO_ORG_RPC_URL");
        instances[currencyId] = yield (0, logic_1.getCroSdk)(currencyId).CroClient.connect(crypto_org_rpc_url);
        return instances[currencyId];
    });
}
exports.getClient = getClient;
/**
 * Extract only the cro amount from list of currencies
 */
const getCroAmount = (amounts, currencyId) => {
    const cryptoOrgCurrency = (0, logic_1.isTestNet)(currencyId) ? sdk_types_1.CryptoOrgTestnetCurrency : sdk_types_1.CryptoOrgCurrency;
    return amounts.reduce((result, current) => current.denom === cryptoOrgCurrency ? result.plus(new bignumber_js_1.BigNumber(current.amount)) : result, new bignumber_js_1.BigNumber(0));
};
exports.getCroAmount = getCroAmount;
/**
 * Get account balances
 */
const getAccount = (addr, currencyId) => __awaiter(void 0, void 0, void 0, function* () {
    const client = yield getClient(currencyId);
    const { header } = yield client.getBlock();
    const crypto_org_indexer = (0, logic_1.isTestNet)(currencyId)
        ? (0, env_1.getEnv)("CRYPTO_ORG_TESTNET_INDEXER")
        : (0, env_1.getEnv)("CRYPTO_ORG_INDEXER");
    let balance = new bignumber_js_1.BigNumber(0);
    let bondedBalance = new bignumber_js_1.BigNumber(0);
    let redelegatingBalance = new bignumber_js_1.BigNumber(0);
    let unbondingBalance = new bignumber_js_1.BigNumber(0);
    let commissions = new bignumber_js_1.BigNumber(0);
    let data;
    try {
        const response = yield (0, network_1.default)({
            method: "GET",
            url: `${crypto_org_indexer}/api/v1/accounts/${addr}`,
        });
        data = response.data;
    }
    catch (error) {
        if ((error === null || error === void 0 ? void 0 : error.status) !== 404) {
            throw error;
        }
    }
    if (data) {
        balance = (0, exports.getCroAmount)(data.result.balance, currencyId);
        bondedBalance = (0, exports.getCroAmount)(data.result.bondedBalance, currencyId);
        redelegatingBalance = (0, exports.getCroAmount)(data.result.redelegatingBalance, currencyId);
        unbondingBalance = (0, exports.getCroAmount)(data.result.unbondingBalance, currencyId);
        commissions = (0, exports.getCroAmount)(data.result.commissions, currencyId);
    }
    return {
        blockHeight: header.height,
        balance: new bignumber_js_1.BigNumber(balance),
        bondedBalance: new bignumber_js_1.BigNumber(bondedBalance),
        redelegatingBalance: new bignumber_js_1.BigNumber(redelegatingBalance),
        unbondingBalance: new bignumber_js_1.BigNumber(unbondingBalance),
        commissions: new bignumber_js_1.BigNumber(commissions),
    };
});
exports.getAccount = getAccount;
/**
 * Get account information for sending transactions
 */
const getAccountParams = (addr, currencyId) => __awaiter(void 0, void 0, void 0, function* () {
    const client = yield getClient(currencyId);
    const { accountNumber, sequence } = yield client.getAccount(addr);
    return {
        accountNumber: accountNumber !== null && accountNumber !== void 0 ? accountNumber : 0,
        sequence: sequence !== null && sequence !== void 0 ? sequence : 0,
    };
});
exports.getAccountParams = getAccountParams;
/**
 * Returns true if account is the signer
 */
function isSender(transaction, addr) {
    return transaction.fromAddress === addr;
}
/**
 * Map transaction to an Operation Type
 */
function getOperationType(messageSendContent, addr) {
    return isSender(messageSendContent, addr) ? "OUT" : "IN";
}
/**
 * Map transaction to a correct Operation Value (affecting account balance)
 */
function getOperationValue(messageSendContent, currencyId) {
    return (0, exports.getCroAmount)(messageSendContent.amount, currencyId);
}
/**
 * Map the send history transaction to a Ledger Live Operation
 */
function convertSendTransactionToOperation(accountId, addr, messageSendContent, transaction, currencyId, memo) {
    const type = getOperationType(messageSendContent, addr);
    return {
        id: (0, operation_1.encodeOperationId)(accountId, messageSendContent.txHash, type),
        accountId,
        fee: new bignumber_js_1.BigNumber((0, exports.getCroAmount)(transaction.fee, currencyId)),
        value: getOperationValue(messageSendContent, currencyId),
        type,
        hash: messageSendContent.txHash,
        blockHash: transaction.blockHash,
        blockHeight: transaction.blockHeight,
        date: new Date(transaction.blockTime),
        senders: [messageSendContent.fromAddress],
        recipients: [messageSendContent.toAddress],
        hasFailed: !transaction.success,
        extra: { memo },
    };
}
/**
 * Fetch operation list
 */
const getOperations = (accountId, addr, startAt, currencyId) => __awaiter(void 0, void 0, void 0, function* () {
    const rawTransactions = [];
    const crypto_org_indexer = (0, logic_1.isTestNet)(currencyId)
        ? (0, env_1.getEnv)("CRYPTO_ORG_TESTNET_INDEXER")
        : (0, env_1.getEnv)("CRYPTO_ORG_INDEXER");
    const { data } = yield (0, network_1.default)({
        method: "GET",
        url: `${crypto_org_indexer}/api/v1/accounts/${addr}/transactions?pagination=offset&page=${startAt + 1}&limit=${PAGINATION_LIMIT}`,
    });
    const accountTransactions = data.result;
    for (let i = 0; i < accountTransactions.length; i++) {
        const msgs = accountTransactions[i].messages;
        const memo = accountTransactions[i].memo;
        const memoTransaction = memo || "";
        for (let j = 0; j < msgs.length; j++) {
            const msgType = msgs[j].type;
            if (msgType.includes(sdk_types_1.CryptoOrgAccountTransactionTypeEnum.MsgSend)) {
                const msgSend = msgs[j].content;
                rawTransactions.push(convertSendTransactionToOperation(accountId, addr, msgSend, accountTransactions[i], currencyId, memoTransaction));
            }
        }
    }
    return rawTransactions;
});
exports.getOperations = getOperations;
/**
 * Broadcast blob to blockchain
 */
const broadcastTransaction = (blob, currencyId) => __awaiter(void 0, void 0, void 0, function* () {
    const client = yield getClient(currencyId);
    const broadcastResponse = yield client.broadcastTx(chain_jslib_1.utils.Bytes.fromHexString(blob).toUint8Array());
    return broadcastResponse;
});
exports.broadcastTransaction = broadcastTransaction;
//# sourceMappingURL=sdk.js.map